import {throwError as observableThrowError, Observable, of} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';

import {TaxplanIQServices} from "./TaxplanIQServices";
import {Session} from "./Session";
import {SOURCE_TYPE} from "../constants/Tpiq.constants";
//import {Partial} from "aws-sdk/clients/cloudsearchdomain";
import {QuestionBase} from "../questionnaires/client-answers/question-based-answers/question-base/question-base";

export function getQuestionsUrl(userId, org, id): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${id}/questions`;
}

export function updateQuestionUrl(userId, org, id, questionId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${id}/questions/${questionId}`;
}

export function questionDragDropUrl(userId, org, id): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${id}/sort`;
}

export function getTabsUrl(userId, org, id): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${id}/sections`;
}

export function getTabsByIdUrl(userId, org, id, tabId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${id}/sections/${tabId}`;
}

export function tabDragDropUrl(userId, org, id): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${id}/sections/sort`;
}

export function getClientQuestionsUrl(org, userId, clientId, qId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${qId}/clients/${clientId}/questions?bucket=`;
}

export function getClientQuestionsBySectionUrl(org, userId, clientId, qId, sectionId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${qId}/clients/${clientId}/sections/${sectionId}/questions?bucket=`;
}

export function saveClientAnswersBySectionUrl(org, userId, clientId, qId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${qId}/clients/${clientId}/questionAnswer`;
}

export function getQnsDocumentUrl(org, userId, channelType, channelId, questionId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/questions/${questionId}/clients/${channelId}/documents`;
}

export function deleteDocumentUrl(org, userId, channelType, channelId, questionId, docId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/questions/${questionId}/clients/${channelId}/documents/${docId}`;
}

export function postMessageUrl(org, userId, channelType, channelId, questionId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/questions/${questionId}/clients/${channelId}/comments`;
}

export function updatePostMessageUrl(org, userId, channelType, channelId, questionId, commentId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/questions/${questionId}/clients/${channelId}/comments/${commentId}`;
}

export function deleteMessageUrl(org, userId, channelType, channelId, questionId, commentId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/questions/${questionId}/clients/${channelId}/comments/${commentId}`;
}

export function getLockQuestionnaireUrl(org, userId, channelType, channelId, questionnaireId, status, bucket): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${questionnaireId}/clients/${channelId}/status?status=${status}&bucket=${bucket}`;
}

export function markAsDone(org, userId, clientId, questionnaireId): string {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/questionnaires/${questionnaireId}/clients/${clientId}/status`;
}

export function getOrgQuestionnaireUrl(orgId, userId): string {
    return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/questionnaires`;
}

export function sendQuestionnaire(orgId, userId, resend){
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/questionnaires/send?resend=${resend}`;
}

export function getQuestionnaireDetailsUrl(orgId, userId){
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/questionnaires/customer`;
}

export function markHasUpdates(orgId, userId, questionnaireId, hasUpdates){
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/questionnaires/${questionnaireId}/mark-updates?hasUpdates=${hasUpdates}`;
}


type ClientQuestions = {
    sections: { id: string, name: string, questions?: Partial<QuestionBase<string | number>>[] }[],
    questions: {
        sectionId: string,
        sectionName: string,
        questionsAnswer: Partial<QuestionBase<string | number>>[]
    },
    recurringFilters: { current: boolean, key: string, type: string, value: string }[],
    done: boolean,
    lock: boolean
};

type ClientQuestionsBySection = {
    sectionId: string,
    sectionName: string,
    questionsAnswer: Partial<QuestionBase<string | number>>[]
};


@Injectable({
    providedIn: 'root'
})
export class QuestionnairesSurveysService extends TaxplanIQServices {

    options = {};
    questionnaireDetails: any;

    constructor(http: HttpClient) {
        super(http);
    }

    /**
     * The one place the external questionnaire-form link is built, so the send
     * dialog and the organizer table cannot drift (CU-868kq8bx5). The link uses
     * the firm's own {org}.taxplaniq.com subdomain and the recipient token; it
     * matches the link EmailHelperService emails on the backend. The retired
     * Qount forms.taxplaniq.com host is not used.
     */
    getPublicFormUrl(org: string, token: string): string {
        return `https://${org}.taxplaniq.com/questionnaire-form/${token}`;
    }

    // Single Questions Services
    getQuestions(userId, org, id): Observable<any> {
        const url = getQuestionsUrl(userId, org, id);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    addNewQuestion(userId, org, id, data): Observable<any> {
        const url = getQuestionsUrl(userId, org, id);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateQuestion(userId, org, id, data): Observable<any> {
        const url = updateQuestionUrl(userId, org, id, data.id);
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    deleteQuestion(userId, org, id, questionId): Observable<any> {
        const url = updateQuestionUrl(userId, org, id, questionId);
        return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    questionDragDrop(userId, org, id, data): Observable<any> {
        const url = questionDragDropUrl(userId, org, id);
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    // Tabs Services
    addTab(userId, org, id, data): Observable<any> {
        const url = getTabsUrl(userId, org, id);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateTab(userId, org, id, data): Observable<any> {
        const url = getTabsByIdUrl(userId, org, id, data.id);
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    deleteTab(userId, org, id, tabId): Observable<any> {
        const url = getTabsByIdUrl(userId, org, id, tabId);
        return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    tabDragDrop(userId, org, id, data): Observable<any> {
        const url = tabDragDropUrl(userId, org, id);
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }


    getClientQuestions(org, userId, clientId, qId, bucket?): Observable<ClientQuestions> {
        let url = getClientQuestionsUrl(org, userId, clientId, qId);
        if (bucket) {
            url = url + bucket;
        }
        return this.query(url, SOURCE_TYPE.JAVA).pipe(
            map((res: any) => {
                const questions: any = res.questions;
                questions.questionsAnswer = questions.questionsAnswer.sort((a, b) => a.questionNo - b.questionNo);
                return {
                    sections: res.sections,
                    questions,
                    recurringFilters: res.recurringFilters,
                    done: res.done,
                    lock: res.lock
                };
            }),
            catchError<ClientQuestions, Observable<never>>((err) => this.handleError(err))
        );
    }

    getClientQuestionsBySection(org, userId, clientId, qId, sectionId, bucket?): Observable<ClientQuestionsBySection> {
        let url = getClientQuestionsBySectionUrl(org, userId, clientId, qId, sectionId);
        if (bucket) {
            url = url + bucket;
        }
        return this.query(url, SOURCE_TYPE.JAVA).pipe(
            map((res: any) => {
                return {
                    sectionId: res.sectionId,
                    sectionName: res.sectionName,
                    questionsAnswer: res.questionsAnswer.sort((a, b) => a.questionNo - b.questionNo)
                };
            }),
            catchError<ClientQuestionsBySection, Observable<never>>((err) => this.handleError(err))
        );
    }


    saveClientAnswers(org, userId, clientId, qId, type, data): Observable<any> {
        let url = saveClientAnswersBySectionUrl(org, userId, clientId, qId);
        if (type === 'submit') {
           url = url + '/submit';
        }
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    uploadDocument(org, userId, channelType, channelId, questionId, data): Observable<any> {
        const url = getQnsDocumentUrl(org, userId, channelType, channelId, questionId);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    deleteDocument(org, userId, channelType, channelId, questionId, docId): Observable<any> {
        const url = deleteDocumentUrl(org, userId, channelType, channelId, questionId, docId);
        return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    createPostMessage(org, userId, channelType, channelId, questionId, data): Observable<any> {
        const url = postMessageUrl(org, userId, channelType, channelId, questionId);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    sendQuestionnaireToClient(org, userId, request, resend) {
      const url = sendQuestionnaire(org, userId, resend);
      return this.create(url, request, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getQuestionnaire(orgId, userId): Observable<any> {
        const url = getOrgQuestionnaireUrl(orgId, userId);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updatePostMessage(org, userId, channelType, channelId, questionId, commentId, data): Observable<any> {
        const url = updatePostMessageUrl(org, userId, channelType, channelId, questionId, commentId);
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    deleteMessage(org, userId, channelType, channelId, postId, commentId): Observable<any> {
        const url = deleteMessageUrl(org, userId, channelType, channelId, postId, commentId);
        return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    lockQuestionnaire(org, userId, channelType, channelId, questionnaireId, status, bucket): Observable<any> {
        const lock = status ? 'lock' : 'unlock';
        const url = getLockQuestionnaireUrl(org, userId, channelType, channelId, questionnaireId, lock, bucket);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    markAsDone(org, userId, clientId, questionnaireId, request): Observable<any> {
        const url = markAsDone(org, userId, clientId, questionnaireId);
        return this.patch(url, request, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    private handleError(error: HttpErrorResponse): any {
        return observableThrowError(error.error);
    }

    getQuestionnaireDetails(org: string, userId: any): Observable<any> {
      if (this.questionnaireDetails && this.questionnaireDetails[userId]){
        return of(this.questionnaireDetails[userId]);
      }
      const url = getQuestionnaireDetailsUrl(org, userId);
      return this.query(url, SOURCE_TYPE.JAVA).pipe(map(res => {
        if (!this.questionnaireDetails){
          this.questionnaireDetails = {};
        }
        this.questionnaireDetails[userId] = res;
        return this.questionnaireDetails[userId];
      }), catchError(e => this.handleError(e)));
    }

    clearQuestionnaireData() {
      this.questionnaireDetails = '';
    }

    markUpdates(userId: string, org: string, questionnaireId: string, hasUpdates: boolean) {
      const url = markHasUpdates(org, userId, questionnaireId, hasUpdates);
      return this.patch(url, {}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }
}
